<template>
    <div class="context-wrapper">

        <div class="card" v-if="system != null && isNationalDataNeeded && contextItemsLoaded">
            <div class="card-block">
                <div class="row">
                    <div class="col-12 col-lg-2">
                        <label>{{ this.$i18n.DATA_CONTEXT }}</label>
                        <a
                            class="ml-1"
                            target="_blank"
                            :href="this.manualUrl"
                        >
                            <Help-circle-outline id="data-context-info" :size="15" />

                            <b-tooltip container="null" target="data-context-info" triggers="hover">
                                {{ this.$i18n.DATA_CONTEXT_INFO }}
                            </b-tooltip>
                        </a>
                    </div>
                    <div class="col-12 col-lg-4 mb-3 mb-lg-0">
                        <dx-select-box
                            :data-source="this.groupedContextItems"
                            :search-enabled="true"
                            :grouped="true"
                            display-expr="name"
                            value-expr="id"
                            ref="contextBox"
                            :value="context.id"
                            :placeholder="this.$i18n.SELECT_CONTEXT"
                            :on-value-changed="handleContextChange"
                            :show-clear-button="true"
                        />
                    </div>
                    <div v-if="showNationalDataToggler" class="col-12 col-lg-6 text-right" v-show="contextItemsLoaded">
                        <label class="mr-3" :class="[!nationalDataLoaded ? 'disabled' : '']">
                            {{ this.$i18n.COMPARE_WITH_NATIONAL_DATA }}
                        </label>
                        <dx-switch
                            v-model="nationalDataSwitchValue"
                            @value-changed="nationalDataSwitchValueChanged"
                            :disabled="!nationalDataLoaded"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from "vuex";

import { DxSelectBox } from "devextreme-vue/select-box";
import { DxSwitch } from "devextreme-vue/switch";

import { BTooltip } from "bootstrap-vue";
import HelpCircleOutline from "vue-material-design-icons/HelpCircleOutline";
import DataSource from "devextreme/data/data_source";

export default {
    components: {
        DxSelectBox,
        DxSwitch,
        BTooltip,
        HelpCircleOutline
    },
    data() {
        return {
            manualUrl: process.env.VUE_APP_MANUAL_URL,
            selectedContext: {},
            previousChangeIgnored: false,
            nationalDataSwitchValue: false,
            groupedContextItems: {},
        };
    },
    computed: {
        ...mapState("kaplanMeierModule", {
            kaplanMeierParameter: (state) => state.kaplanMeierParameter,
        }),
        ...mapState("contextModule", {
            context: (state) => state.context,
            contextItems: (state) => state.contextItems,
            contextItemsLoaded: (state) => state.contextItemsLoaded,
        }),
        ...mapState("tumourDataModule", {
            tumourDataLoaded: (state) => state.dataLoaded,
            tumourNationalDataLoaded: (state) => state.nationalDataLoaded,
        }),
        ...mapState("rhinoDataModule", {
            rhinoDataLoaded: (state) => state.dataLoaded,
            rhinoNationalDataLoaded: (state) => state.nationalDataLoaded,
        }),
        ...mapState("ciDataModule", {
            ciDataLoaded: (state) => state.dataLoaded,
            ciNationalDataLoaded: (state) => state.nationalDataLoaded,
        }),
        ...mapState("tagDataModule", {
            tagDataLoaded: (state) => state.dataLoaded,
            tagNationalDataLoaded: (state) => state.nationalDataLoaded,
        }),
        ...mapState("dataModule", {
            system: (state) => state.system,
            isNationalDataNeeded: (state) => state.system.isNationalDataNeeded,
            showNationalData: (state) => state.showNationalData,
        }),
        ...mapState({
            advancedQueryCriteria: (state) => state.advancedQueryCriteria,
            isAdmin: (state) => state.isAdmin,
        }),
        nationalDataLoaded: function () {
            let nationalDataLoaded = null;
            switch (this.evalType) {
                case "tumour":
                    nationalDataLoaded = this.tumourNationalDataLoaded;
                    break;
                case "rhino":
                    nationalDataLoaded = this.rhinoNationalDataLoaded;
                    break;
                case "ci":
                    nationalDataLoaded = this.ciNationalDataLoaded;
                    break;
                case "tag":
                    nationalDataLoaded = this.tagNationalDataLoaded;
                    break;
            }
            return nationalDataLoaded;
        },
    },
    watch: {
        context: function () {
            // when context changes,
            // switch off national data displaying
            this.nationalDataSwitchValue = false;
            this.setNationalData(false);

            this.setDataLoaded(false);
            this.setTreeListDataLoaded(false);
        },
        contextItems: function () {
            var temp = this.contextItems.map((o) => {
                return {
                    ...o,
                    // adding 'administrator' section 
                    // to the dropdown with 'Select all...'
                    // option, to retrieve all data (no context id/type)
                    displayType: o.type == null && this.isAdmin
                        ? this.$i18n.ADMINISTRATOR
                        : `${o.type.charAt(0).toUpperCase()}${o.type.slice(1)}s`
                };
            });

            this.groupedContextItems = new DataSource({
                store: temp,
                key: "id",
                group: "displayType"
            });
        },
    },
    methods: {
        ...mapActions("dataModule", ["setNationalData"]),
        ...mapActions("tumourDataModule", ["setDataLoaded"]),
        ...mapActions("contextModule", ["setContext"]),
        ...mapActions("kaplanMeierModule", ["readKaplanMeierEstimates"]),
        ...mapActions("treeListDataModule", ["setTreeListDataLoaded"]),
        handleContextChange(e) {
            if (this.context.updatedManually && !this.previousChangeIgnored) {
                this.previousChangeIgnored = true;
                return;
            }

            const contextFound = this.contextItems.filter((o) => o.id === e.value)[0];

            const newContext = {
                id: contextFound.id,
                type: contextFound.type,
            };

            this.setContext(newContext);
        },
        nationalDataSwitchValueChanged(e) {
            this.setNationalData(e.value);
        },
    },
    mounted() {
        if (this.showNationalData) this.nationalDataSwitchValue = true;
    },
    props: {
        evalType: String,
        showNationalDataToggler: {
            type: Boolean,
            default: true
        }
    },
};
</script>

<style>
label {
    line-height: 2.5em;
}
label.disabled {
    color: #c3c3c3;
}
.onoffswitch-inner:before {
    background-color: #009efb;
}
.onoffswitch {
    top: 3px;
}
.onoffswitch-label {
    border: 0;
    border-radius: 0;
}
.onoffswitch-switch {
    border: 2px solid #009efb;
}
.onoffswitch-label.disabled,
.onoffswitch-label.disabled .onoffswitch-switch {
    border: 2px solid #ccc;
    cursor: not-allowed;
}
</style>
